import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { midas } from '@resurs/midas'
import { Column, Columns, Container } from '@resurs/midas-react'

import { DataTable } from '../../midasComponents'

import Page from '../../internal/Page'
import { getText } from '../../internal/TextLabels'
import style from './shared/transactions.style'
import pageableTable from '../../higher-order/pageable-table'
import searchableTable from '../../higher-order/searchable-table'

import { ChannelTypes, TransactionHeaders } from '../../models/transaction'
import { PaymentMethods } from '../../models/payment-methods'
import govIdFormat from '../../util/govIdFormat'
import {
  TransactionResultTypes,
  TransactionStatus,
  TransactionTypes,
} from '../../util/transactionConstants'
import { debit, disableCheckbox } from './shared/transaction-utils'
import CurrencyFormat from '../../components/common/CurrencyFormat'
import { AuthContext } from '../../context/AuthContext'
import { Authorities } from '../../models/authorities'
import { usePagination } from '../../provider/PaginationProvider'
import LoaderOverlay from '../../internal/LoaderOverlay'

const transactionIsDebitable = transaction => {
  return (
    transaction.payment &&
    transaction.payment.paymentStatus &&
    transaction.payment.paymentStatus.debitableAmount > 0
  )
}

const getTransactionStatus = transaction => {
  return transaction.payment && transaction.payment.paymentStatus
    ? transaction.payment.paymentStatus.status
    : 'NOT_DEBITABLE'
}

const getTransactionStatusText = transaction => {
  switch (transaction.resultType) {
    case TransactionResultTypes.RESERVED_TRANSACTION:
      return getText('transactions.reserved')
    case TransactionResultTypes.BOOKED_TRANSACTION:
      return transaction.amount < 0 &&
        transaction.type === TransactionTypes.CREDIT
        ? getText('transactions.credited')
        : getText('transactions.booked')
    default:
  }

  switch (transaction.status) {
    case TransactionStatus.ANNULLED:
      return getText('transactions.annulled')
    case TransactionStatus.FROZEN:
      return getText('transactions.frozen')
    default:
      return getText(`transactions.${getTransactionStatus(transaction)}`)
  }
}

const TransactionsUI = props => {
  const { hasAuthority } = useContext(AuthContext)
  const { paginationData } = usePagination()

  const channelTypes = new ChannelTypes()
  const headers = new TransactionHeaders()

  const [invoiceNumbers, setInvoiceNumbers] = useState({})

  const renderTableBody = () => {
    let tableBody = []
    if (paginationData?.results && paginationData.results.length > 0) {
      tableBody = paginationData.results.map(transaction => (
        <tr
          data-checkbox-disabled={
            disableCheckbox(transaction, props.hasInvoiceNumber, invoiceNumbers)
              ? 'true'
              : 'false'
          }
          key={transaction.id || Math.random()}
          onClick={() => {
            props.onClick(transaction)
          }}
        >
          <td style={{ height: '55px' }}>
            {transaction.externalId}
            {!transaction.externalId &&
              transaction.type &&
              transaction.type === TransactionTypes.DEBIT &&
              getText('transactions.debit')}
            {!transaction.externalId &&
              transaction.type &&
              transaction.type === TransactionTypes.CREDIT &&
              getText('transactions.credit')}
          </td>
          <td>
            {transaction.created &&
              `${moment(transaction.created).format('L LT')}`}
            {!transaction.created &&
              transaction.date &&
              `${moment(transaction.date).format('L')}`}
          </td>

          <td>
            {(transaction.customer &&
              transaction.customer.governmentId &&
              govIdFormat(transaction.customer.governmentId)) ||
              (transaction.governmentID &&
                govIdFormat(transaction.governmentID))}
          </td>

          <td>
            {transaction.customer &&
              transaction.customer.customerGivenAddress &&
              transaction.customer.customerGivenAddress.name &&
              transaction.customer.customerGivenAddress.name.full}
          </td>

          <td>
            {transaction.channelType &&
              channelTypes.labels[transaction.channelType]}
            {transaction.resultType !== 'PAYMENT' &&
              channelTypes.labels['MERCHANT_PORTAL']}
          </td>
          {!props.hasInvoiceNumber && (
            <td>
              {transactionIsDebitable(transaction) &&
              transaction.paymentMethod.type ===
                PaymentMethods.RESURS_INVOICE ? (
                <input
                  className={[midas.formInput, midas.inputSm].join(' ')}
                  id={transaction.id}
                  value={
                    invoiceNumbers[transaction.id]
                      ? invoiceNumbers[transaction.id]
                      : ''
                  }
                  placeholder={getText('transactions.invoiceNumberMissing')}
                  onChange={e =>
                    setInvoiceNumbers({
                      ...invoiceNumbers,
                      [transaction.id]: e.target.value,
                    })
                  }
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                ''
              )}
            </td>
          )}
          <td>
            {transaction.paymentMethod &&
            transaction.paymentMethod.provider &&
            transaction.paymentMethod.provider.toLowerCase() !== 'resurs'
              ? transaction.paymentMethod.provider
              : transaction.paymentMethod.description}
          </td>
          <td style={style.tableNumber}>
            <CurrencyFormat
              value={transaction?.payment?.paymentStatus?.totalOrderAmount}
            />
            <CurrencyFormat value={transaction?.amount} />
          </td>
          <td>{getTransactionStatusText(transaction)}</td>
        </tr>
      ))
    }
    return tableBody
  }

  const transactionsTable = (
    <DataTable
      hover
      striped
      wrap
      fixed
      searchTerm={paginationData?.searchQuery}
      searchTitle={getText('default.search')}
      rowsPerPageTitle={getText('default.rowsPerPage')}
      lastSort={
        paginationData?.sort
          ? {
              headerName: paginationData.sort.split(',')[0],
              direction: paginationData.sort.split(',')[1],
            }
          : null
      }
      actionButton={{
        label: getText('transactions.action.options.debitAll'),
        onClick: items => {
          debit(
            items,
            paginationData.results,
            props.hasInvoiceNumber,
            invoiceNumbers,
            props.debit,
          )
        },
        loading: props.debitLoading,
        callbackOnClear: () => props.loadTransactions(),
      }}
      pagination={{
        currentPage: props.pagination.pageNumber + 1,
        totalPages: props.pagination.totalPages,
        rowsPerPage: props.pagination.size,
        totalCount: props.pagination.totalElements,
      }}
      onNext={props.onNext}
      onPrev={props.onPrev}
      onRowsPerPageChange={size => props.onChangeSize(size)}
      onSort={(currentHeaderId, direction) => {
        props.onChangeSortPriority(currentHeaderId, direction)
      }}
      onSearch={sTerm => props.onLoadSearchData(sTerm)}
      search
      placeholder={getText('transactions.search.label')}
      headers={
        !props.hasInvoiceNumber ? headers.InvoiceHeaders : headers.Headers
      }
      disableRowActions={!hasAuthority(Authorities.TRANSACTIONS_DEBIT)}
      dataCy="transaction-list"
    >
      <tbody>{renderTableBody()}</tbody>
    </DataTable>
  )

  return (
    <Page title={getText('transactions.title')} dataCy="transactions-page">
      {props.filtering}
      <Container>
        <Columns>
          <Column col12 noPadding>
            <LoaderOverlay loading={props.isLoading}>
              {transactionsTable}
            </LoaderOverlay>
          </Column>
        </Columns>
      </Container>
    </Page>
  )
}

TransactionsUI.propTypes = {
  onClick: PropTypes.func.isRequired,
  onChangeSortPriority: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onChangeSize: PropTypes.func.isRequired,
  pagination: PropTypes.object.isRequired,
  onLoadSearchData: PropTypes.func.isRequired,
  debit: PropTypes.func.isRequired,
  filtering: PropTypes.object.isRequired,
  debitLoading: PropTypes.bool.isRequired,
  loadTransactions: PropTypes.func.isRequired,
  hasInvoiceNumber: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
}

export default searchableTable(pageableTable(TransactionsUI))
